import { Button, Chip, Link, Paper, Stack, Typography } from "@mui/material";

/**
 * The small pieces Builder's pages share: the chat page and the app page draw the same
 * panel frame, the same live link and the same list of what an app is allowed to do, and
 * two copies of any of them would be two slightly different stories about one app.
 */

export const RUNNING = ["running", "rescheduling"];

export function consoleUrl() {
  return (window.KERNEL_CONFIG && window.KERNEL_CONFIG.console) || "https://console.lopie.one";
}

export function money(cents) {
  return `$${((cents || 0) / 100).toFixed(2)}`;
}

/** A titled box beside the chat. */
export function Panel({ title, children, basis }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, flexShrink: 0, minWidth: 0 }}>
      <Stack spacing={1.25}>
        <Typography variant="overline" color="text.secondary">{title}</Typography>
        {children}
        {basis ? (
          <Typography variant="caption" color="text.secondary">{basis}</Typography>
        ) : null}
      </Stack>
    </Paper>
  );
}

/** Where an approved app lives, from the control plane's own status rows. */
export function LiveLinks({ instances }) {
  const ready = (instances || []).filter((i) => i.url);
  if (!instances?.length) {
    return (
      <Typography variant="body2">
        Approved. It is not installed yet; it appears here once it is, and on
        the{" "}
        <Link href={`${consoleUrl()}/#installs`} target="_blank" rel="noreferrer">
          console's Installs screen
        </Link>
        .
      </Typography>
    );
  }
  return (
    <Stack spacing={0.75}>
      {instances.map((i) => (
        <Stack key={i.name} direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
          {i.url && i.status === "ready" ? (
            <Button size="small" variant="contained" href={i.url} target="_blank" rel="noreferrer">
              Open {i.name}
            </Button>
          ) : (
            <Typography variant="body2">{i.name}</Typography>
          )}
          <Chip size="small" variant="outlined" label={i.status === "ready" ? "live" : i.status} />
          {i.version ? <Chip size="small" variant="outlined" label={`v${i.version}`} /> : null}
        </Stack>
      ))}
      {!ready.length ? (
        <Typography variant="caption" color="text.secondary">
          Installing. The link appears when it is ready, usually within a few minutes.
        </Typography>
      ) : null}
    </Stack>
  );
}

/**
 * What an app asks to be allowed to do, as a list of sentences and the one line under it
 * that says what the list does not mean. The chat page frames it as a panel beside the
 * conversation and the app page as a section; the sentences are the same.
 */
export function AllowedList({ lines, basis, empty = "Nothing asked for yet. This fills in when the builder requests permission." }) {
  return (
    <>
      {lines.length ? (
        <Stack component="ul" spacing={0.75} sx={{ m: 0, pl: 2.5 }}>
          {lines.map((line) => (
            <Typography component="li" variant="body2" key={line}>{line}</Typography>
          ))}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">{empty}</Typography>
      )}
      {lines.length && basis ? (
        <Typography variant="caption" color="text.secondary">{basis}</Typography>
      ) : null}
    </>
  );
}

/** What the list of what an app is allowed to do does not mean, said once, under it. */
export const ALLOWED_BASIS = "What the builder asked for. Nothing is granted until a governor approves it. Whoever opens the app still only sees what they could already see, and a person who is missing a tag it reads or writes with can't open it at all.";
